import Sticker, { StickerIcon } from '@@types/Sticker';

import { Emoticon } from '~/constants';
import { EmoticonDetail, StickerDetail } from '~/services/EmoticonStickerDetail';
import { EmoticonTab, StickerTab } from '~/services/EmoticonStickerTab';
import Actions from '~/stores/Actions';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { emoticonStickerStore } from '~/stores/EmoticonStickerStore';
import { api } from '~/stores/providers/Api2';
import { getBeforeSpace } from '~/utils/editorUtil';
import { computed } from 'mobx';
import { tap } from 'ramda';

class EmoticonStickerActions {
  constructor(private actions: Actions) {}

  public selectSticker = (pack: Sticker, sticker: StickerIcon) => {
    const channelId = this.activeChannelId;
    this.logActions
      .fetchLatestLogs(channelId)
      .then(() => this.logActions.sendSticker(channelId, this.makeStickerParam(pack, sticker)));

    emoticonStickerStore.activePopperState?.setOpen(false);
  };

  public selectEmoticon = (emoji: string) => {
    const editor = this.actions.editorActions.getEditorById(emoticonStickerStore.activePopperState?.editorId);
    if (!editor) {
      return;
    }
    editor.replaceSelection(`${getBeforeSpace(editor)}:${emoji}:`);
    this.actions.editorActions.focusEditor(editor);
    editor.scrollIntoView(editor.cursorCoords() as { left: number; top: number; right: number; bottom: number });

    emoticonStickerStore.activePopperState?.setOpen(false);
  };

  private emoticonTab = new EmoticonTab(emoticonStickerStore);
  private stickerTab = new StickerTab(emoticonStickerStore);
  private emoticonDetail = new EmoticonDetail(emoticonStickerStore, this.selectEmoticon);
  private stickerDetail = new StickerDetail(emoticonStickerStore, this.selectSticker);

  @computed
  get activeTab() {
    return emoticonStickerStore.activePopperState.tab === Emoticon.Sticker ? this.stickerTab : this.emoticonTab;
  }

  @computed
  get activeDetail() {
    return emoticonStickerStore.activePopperState.tab === Emoticon.Sticker ? this.stickerDetail : this.emoticonDetail;
  }

  @computed
  private get activeChannelId() {
    return emoticonStickerStore.isOpenedMain
      ? mainDetailUiState.contentsChannelId
      : threadDetailUiState.contentsChannelId;
  }

  @computed
  private get logActions() {
    return emoticonStickerStore.isOpenedMain ? this.actions.logActions : this.actions.threadLogActions;
  }

  public fetchStickers = () => {
    return api.sticker.fetchSticker().then(tap(emoticonStickerStore.setStickersFromRes));
  };

  private makeStickerParam = (pack: Sticker, sticker: StickerIcon) => {
    return {
      prefixUrl: pack.prefixUrl,
      stickerPackId: pack.id,
      stickerId: sticker.id,
    };
  };

  public close = () => {
    emoticonStickerStore.activePopperState.setOpen(false);
  };

  public moveLeft = () => {
    if (this.activeTab.isFocused()) {
      this.activeTab.left();
      return;
    }

    if (this.activeDetail.canMoveLeftOnly()) {
      this.activeDetail.left();
      return;
    }

    if (this.activeTab.canMoveLeft()) {
      this.activeTab.left();
      this.activeDetail.onChangeTabLeft();
    }
  };

  public moveRight = () => {
    if (this.activeTab.isFocused()) {
      this.activeTab.right();
      return;
    }

    if (this.activeDetail.canMoveRightOnly()) {
      this.activeDetail.right();
      return;
    }

    if (this.activeTab.canMoveRight()) {
      this.activeTab.right();
      this.activeDetail.onChangeTabRight();
    }
  };

  public moveTab = (isShiftPressed = false) => {
    if (isShiftPressed) {
      this.activeDetail.shiftTab();
    } else {
      this.activeTab.tab();
    }
  };

  public moveUp = () => {
    this.activeDetail.up();
  };

  public moveDown = () => {
    if (this.activeTab.isFocused()) {
      this.activeTab.down();
    } else {
      this.activeDetail.down();
    }
  };

  public enter = () => {
    if (this.activeTab.isFocused()) {
      this.activeTab.enter();
    } else {
      this.activeDetail.enter();
    }
  };
}

export default EmoticonStickerActions;
